import React, { useEffect, useState } from 'react';
import { captureMessage } from "@sentry/react";

import API from '../API';
import { Apify } from '../Apify';
import { Container, Row, Col } from 'reactstrap';
import { T, TraksConsumer } from '../traks';
import { default_currency } from '../Site';
import { ConnectedCheckout } from '../c/PremiumKeys/Checkout/Checkout';
import { OrderOverview } from '../c/PremiumKeys/Order/OrderOverview';
import { PaymentFrame } from '../c/PremiumKeys/Payment/PaymentFrame';
import { Cart } from '../c/PremiumKeys/Cart/Cart';
import { ThankYou } from '../c/PremiumKeys/ThankYou/ThankYou';
import { PaymentError } from '../c/PremiumKeys/Payment/PaymentError';
import { consumePendingCartGameId } from '../misc';
import Me from '../Me';

const getParamValue = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value !== null) {
      const trimmed = value.trim();
      if (trimmed !== '') return trimmed;
    }
  }
  return null;
};

const parsePositiveInt = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const parseGameId = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const safeJsonParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    localStorage.removeItem('cart');
    captureMessage("Invalid cart JSON in localStorage", {
      level: "warning",
      extra: {
        key: "cart",
        rawLength: raw.length
      }
    });
    return null;
  }
};

const isValidDateString = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const normalizeStartDate = (value) => {
  if (!value) return null;
  if (isValidDateString(value)) return value;
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (match) {
    const normalized = `${match[3]}-${match[2]}-${match[1]}`;
    return isValidDateString(normalized) ? normalized : null;
  }
  return null;
};

const getTodayString = () => new Date().toISOString().split('T')[0];

// NOTE: parses query params and may persist values to localStorage to reuse existing flow.
const parseCartQueryParams = (search) => {
  const params = new URLSearchParams(search);

  const rawPackage = getParamValue(params, ['package_id', 'packageId', 'package', 'pkg']);
  const rawKeys = getParamValue(params, ['keys']);
  const rawMonths = getParamValue(params, ['months']);
  const rawStartDate = getParamValue(params, ['start_date', 'startdate', 'startDate']);
  const rawUpgradeMonths = getParamValue(params, ['upgrade_months']);
  const rawGameId = getParamValue(params, ['game_id', 'game-id']);

  const initialKeys = parsePositiveInt(rawKeys);
  const parsedMonths = parsePositiveInt(rawMonths);
  const upgradeMonths = parsePositiveInt(rawUpgradeMonths);
  let initialMonths = parsedMonths;
  if (initialMonths === null && upgradeMonths !== null) {
    initialMonths = upgradeMonths;
    localStorage.setItem('upgrade_months', String(upgradeMonths));
  }

  let initialPackageId = null;
  if (rawPackage) {
    const parsedPackageId = parseInt(rawPackage, 10);
    if (Number.isFinite(parsedPackageId) && parsedPackageId > 0) {
      initialPackageId = parsedPackageId;
    }
  }

  let initialStartDate = null;
  if (rawStartDate) {
    initialStartDate = normalizeStartDate(rawStartDate) || getTodayString();
  }

  let gameIdFromUrl = null;
  const parsedGameId = parseGameId(rawGameId);
  if (parsedGameId !== null) {
    gameIdFromUrl = parsedGameId;
    localStorage.setItem('cart-game', String(parsedGameId));
  } else {
    const pendingGameId = parseGameId(consumePendingCartGameId());
    if (pendingGameId !== null) {
      gameIdFromUrl = pendingGameId;
      localStorage.setItem('cart-game', String(pendingGameId));
    } else {
      const storedGameId = parseGameId(localStorage.getItem('cart-game'));
      if (storedGameId !== null) {
        gameIdFromUrl = storedGameId;
      }
    }
  }

  return {
    initialKeys,
    initialMonths,
    initialStartDate,
    initialPackageId,
    gameIdFromUrl,
  };
};

const CartPage = () => {
  const [cart, setCart] = useState({});
  const [game, setGame] = useState(null);
  const [orderId, setOrderId] = useState('');
  const [view, setView] = useState('order');
  const [orderDetails, setOrderDetails] = useState({});
  const [preDefinedMonths, setPreDefinedMonths] = useState(null);
  const [initialKeys, setInitialKeys] = useState(null);
  const [initialMonths, setInitialMonths] = useState(null);
  const [initialStartDate, setInitialStartDate] = useState(null);
  const [initialPackageId, setInitialPackageId] = useState(null);
  const [autoAddFromUrl, setAutoAddFromUrl] = useState(false);
  const vatRate = 25;

  const handlePaymentComplete = (order) => {
    API.post('/a/update_payment', {
      orderId: order.orderId.replace(/"/g, ''),
      orderStatus: order.data.message
    }, (response, status) => {
      if (status !== 200) {
        captureMessage("Payment update failed", {
          level: "error",
          extra: {
            orderId: orderId,
            response: response,
            status: status
          }
        });
        return;
      }

      if (response && response.error) {
        captureMessage("Payment update error", {
          level: "error",
          extra: {
            orderId: orderId,
            response: response,
            status: status
          }
        });
        return;
      }

      setView('thankyou');
    });
  };

  const handlePaymentFailed = () => {
    API.post('/a/update_payment', {
      orderId: orderId.replace(/"/g, ''),
      orderStatus: 'Cancelled'
    }, (response, status) => {
      if (status !== 200) {
        captureMessage("Payment cancellation failed", {
          level: "error",
          extra: {
            orderId: orderId,
            response: response,
            status: status
          }
        });
      }

      if (response && response.error) {
        captureMessage("Payment cancellation error", {
          level: "error",
          extra: {
            orderId: orderId,
            response: response,
            status: status
          }
        });
      }

      setView('error');
    });
  };

  useEffect(() => {
    const hasQuery = window.location.search && window.location.search.length > 1;
    const {
      initialKeys: parsedKeys,
      initialMonths: parsedMonths,
      initialStartDate: parsedStartDate,
      initialPackageId: parsedPackageId,
      gameIdFromUrl,
    } = parseCartQueryParams(window.location.search);

    if (parsedKeys) setInitialKeys(parsedKeys);
    if (parsedMonths) setInitialMonths(parsedMonths);
    if (parsedStartDate) setInitialStartDate(parsedStartDate);
    if (parsedPackageId) setInitialPackageId(parsedPackageId);
    if (gameIdFromUrl !== null) setGame(gameIdFromUrl);

    if (hasQuery) {
      setAutoAddFromUrl(true);
      window.history.replaceState({}, '', '/cart');
    }
  }, []);

  useEffect(() => {
    const state = localStorage.getItem('cart');
    const cartState = safeJsonParse(state);
    if (cartState !== null) setCart(cartState);
  }, []);

  useEffect(() => {
    const upgradeMonths = Number(localStorage.getItem("upgrade_months"));
    if (upgradeMonths) {
      setPreDefinedMonths(upgradeMonths);
      localStorage.removeItem("upgrade_months");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (autoAddFromUrl && view === 'cart') {
      setAutoAddFromUrl(false);
    }
  }, [autoAddFromUrl, view]);

  const setHeaderBreadcrumb = () => {
    const steps = ['order', 'cart', 'checkout', 'payment', 'thankyou'];
    const labels = {
      order: <T>Order</T>,
      cart: <T>Your Cart</T>,
      checkout: <T>Checkout</T>,
      payment: <T>Payment</T>,
      thankyou: <T>Thank You</T>
    };

    return (
      <Row className="cart-breadcrumb">
        {steps.map((step, index) => {
          const isActive = step === view;
          return (
            <span key={step} className={`cart-breadcrumb-step ${isActive ? 'active' : ''}`}>
              {labels[step]}
              {index < steps.length - 1 && <span className="cart-breadcrumb-separator"> &gt; </span>}
            </span>
          );
        })}
      </Row>
    );
  };

  const MyKeys = Apify("/a/my_keys")
    .with_response_prop("my_keys")
    .wrap((props) => {
        const me = props.me;

        if (me.User.Subscription === 'free') {
            return null;
        }

		const premiumKeys = props.my_keys.AvailablePremiumKeys;
        const totalKeys = props.my_keys.TotalPremiumKeys;

        return (
			<React.Fragment>
                <Col xs="12">
                    <span className="cart-key-status p-4">
                        <T>You have used <strong>{totalKeys - premiumKeys}</strong> of <strong>{totalKeys}</strong> premium keys.</T>
                    </span>
                </Col>
			</React.Fragment>
        )
    })

  const displayViewContent = () => {
    if (view === 'cart') {
      return <Cart cart={cart} setCart={setCart} setView={setView} defaultCurrency={default_currency} vatRate={vatRate} />;
    } else if (view === 'checkout') {
      return (
        <ConnectedCheckout
          cart={cart}
          setCart={setCart}
          setView={setView}
          orderId={orderId}
          setOrderId={setOrderId}
          game={game}
          defaultCurrency={default_currency}
          setOrderDetails={setOrderDetails}
          vatRate={vatRate}
          onComplete={(data) => handlePaymentComplete(data)}
        />
      );
    } else if (view === 'order') {
      return (
        <TraksConsumer>
          <OrderOverview
            cart={cart}
            setCart={setCart}
            setView={setView}
            game={game}
            setGame={setGame}
            vatRate={vatRate}
            preDefinedMonths={preDefinedMonths}
            initialKeys={initialKeys}
            initialMonths={initialMonths}
            initialStartDate={initialStartDate}
            initialPackageId={initialPackageId}
            autoAddFromUrl={autoAddFromUrl}
          />
        </TraksConsumer>
      );
    } else if (view === 'payment') {
      return (
        <PaymentFrame
          orderId={orderId}
          onComplete={(data) => handlePaymentComplete(data)}
          setView={setView}
          onError={() => handlePaymentFailed()}
        />
      );
    } else if (view === 'thankyou') {
      return <ThankYou cart={cart} setCart={setCart} setGame={setGame} orderDetails={orderDetails} />;
    } else if (view === 'error') {
      return <PaymentError setView={setView} />;
    }
    return null;
  };

  const me = Me.get();

  return (
    <Container>
      <Row className="py-4">
        <Col xs="12">{setHeaderBreadcrumb()}</Col>
      </Row>
      <Row className='ad_game_box pt-4 container-fluid mb-5'>
        {view === 'order' && me && <MyKeys me={me} />}
        <Col xs="12">
          {displayViewContent()}
        </Col>
      </Row>
    </Container>
  );
};

export default CartPage;



// WEBPACK FOOTER //
// ./src/p/Cart.js